import {
	LineChart, Line, XAxis, YAxis, Tooltip, ReferenceArea, ResponsiveContainer, Brush
} from 'recharts';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Button, Input, Typography, AlertColor } from '@mui/material';
import { Download, Check, Clear, Save } from '@mui/icons-material';
import html2canvas from 'html2canvas';
import { chartsConf, chartsConfigData } from '../../../services/api/charts/charts';
import {
	BrushRange,
	BrushDragEndInput,
	buildReadingDates,
	buildReadingLookup,
	formatChartDate,
	getBrushDragEndPayload,
	getChartDateKey,
	getInitialBrushRange,
} from './chartUtils';

type Entrada = {
	data_leitura: string;
	[key: string]: any;
};

type InstrumentoMarco = {
	nome: string;
	leituras: Entrada[];
	corLinha: string;
	limites: {
		x: Array<number | null>;
		y: Array<number | null>;
		z: Array<number | null>;
	};
};

type Props = {
	instrumentos: InstrumentoMarco[];
	barragem_id: string;
	confGrafico: chartsConfigData[] | null;
	tipo_instrumento: string;
	alert: (menssage: string, severity: AlertColor) => void;
};

const parseNumber = (value: any) => {
	if (typeof value === 'string') {
		const clean = value.replace(',', '.');
		const parsed = Number(clean);
		return Number.isNaN(parsed) ? null : parsed;
	}

	if (typeof value === 'number') {
		return Number.isNaN(value) ? null : value;
	}

	return null;
};

const getBounds = (values: number[], limits: Array<number | null>, includeZero: boolean): [number, number] => {
	const validLimits = limits.filter((value): value is number => value !== null);
	const combined = [...values, ...validLimits, ...(includeZero ? [0] : [])];

	if (combined.length === 0) {
		return [0, 1];
	}

	const min = Math.min(...combined);
	const max = Math.max(...combined);
	const diff = max - min || Math.abs(max || 1);
	const margin = diff * 0.1;

	return [Math.max(0, min - margin), max + margin];
};

const renderFaixas = (
	limites: Array<number | null>,
	max: number,
	yAxisId: string
) => {
	const [verde, amarelo, laranja, vermelho] = limites;

	return (
		<>
			{verde !== null && <ReferenceArea yAxisId={yAxisId} y1={0} y2={verde} fill="green" fillOpacity={0.2} />}
			{verde !== null && amarelo !== null && <ReferenceArea yAxisId={yAxisId} y1={verde} y2={amarelo} fill="yellow" fillOpacity={0.2} />}
			{amarelo !== null && laranja !== null && <ReferenceArea yAxisId={yAxisId} y1={amarelo} y2={laranja} fill="orange" fillOpacity={0.2} />}
			{laranja !== null && <ReferenceArea yAxisId={yAxisId} y1={laranja} y2={vermelho ?? max} fill="red" fillOpacity={0.2} />}
		</>
	);
};

const EixoGrafico = ({
	data,
	label,
	campo,
	ativos,
	mostrarFaixas,
	limites,
	domain,
	loading,
	showBrush,
	brushRange,
	onBrushDragEnd
}: {
	data: any[];
	label: string;
	campo: 'x' | 'y' | 'z';
	ativos: InstrumentoMarco[];
	mostrarFaixas: boolean;
	limites: Array<number | null>;
	domain: [number, number];
	loading: boolean;
	showBrush: boolean;
	brushRange?: BrushRange;
	onBrushDragEnd?: (event: BrushDragEndInput) => void;
}) => {
	const currentBrushRange = brushRange ?? { startIndex: 0, endIndex: 0 };
	const brushStartIndex = data.length > 0 ? Math.min(currentBrushRange.startIndex, data.length - 1) : 0;
	const brushEndIndex = data.length > 0 ? Math.min(Math.max(currentBrushRange.endIndex, brushStartIndex), data.length - 1) : 0;

	if (loading) {
		return (
			<div style={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				height: '300px'
			}}>
				<div style={{
					border: '4px solid #f3f3f3',
					borderTop: '4px solid #1976d2',
					borderRadius: '50%',
					width: '40px',
					height: '40px',
					animation: 'spin 1s linear infinite'
				}} />
			</div>
		);
	}

	return (
		<ResponsiveContainer width="100%" height={300}>
			<LineChart data={data} syncId="marco">
				<XAxis
					dataKey="data_leitura"
					fontFamily='Roboto'
					height={60}
					interval="preserveStartEnd"
					tick={({ x, y, payload }: { x: number; y: number; payload: { value: string } }) => (
						<g transform={`translate(${x},${y})`}>
							<text
								x={0}
								y={0}
								dy={10}
								textAnchor="end"
								fill="#666"
								fontSize={11}
								fontFamily="Roboto"
								transform="rotate(-45)"
							>
								{formatChartDate(payload.value)}
							</text>
						</g>
					)}
				/>
				<YAxis
					yAxisId="left"
					domain={domain}
					label={{ value: label, angle: -90, dx: -30, style: { fontFamily: 'Roboto' } }}
					tickFormatter={(value) => Number(value).toFixed(2).replace('.', ',')}
					fontFamily='Roboto'
				/>
				<Tooltip
					formatter={(value: number) => value != null ? value.toFixed(2).replace('.', ',') : ''}
					contentStyle={{ fontFamily: 'Roboto' }}
					itemStyle={{ fontFamily: 'Roboto' }}
					labelStyle={{ fontFamily: 'Roboto' }}
					labelFormatter={formatChartDate}
				/>
				<Brush
					dataKey="data_leitura"
					fontFamily='Roboto'
					height={showBrush ? 40 : 0}
					startIndex={brushStartIndex}
					endIndex={brushEndIndex}
					onDragEnd={showBrush ? onBrushDragEnd : undefined}
					tickFormatter={formatChartDate}
					style={showBrush ? undefined : { opacity: 0, pointerEvents: 'none' }}
				/>

				{mostrarFaixas && renderFaixas(limites, domain[1], 'left')}

				{ativos.map((instrumento) => (
					<Line
						key={`${instrumento.nome}_${campo}`}
						type="monotone"
						dataKey={`${instrumento.nome}_${campo}`}
						stroke={instrumento.corLinha}
						yAxisId="left"
						strokeWidth={2}
						dot={{ r: 2 }}
						connectNulls
						fontFamily='Roboto'
					/>
				))}
			</LineChart>
		</ResponsiveContainer>
	);
};

const GraficoLinhasMarcoMulti = ({ instrumentos, alert, barragem_id, confGrafico, tipo_instrumento }: Props) => {
	const [selecionados, setSelecionados] = useState<string[]>([]);
	const [loading, setLoading] = useState(false);
	const [yMinCustom, setYMinCustom] = useState<string>('');
	const [yMaxCustom, setYMaxCustom] = useState<string>('');
	const [yMinAplicado, setYMinAplicado] = useState<number | null>(null);
	const [yMaxAplicado, setYMaxAplicado] = useState<number | null>(null);
	const [confId, setConfId] = useState<string | null>(null);
	const [committedBrushRange, setCommittedBrushRange] = useState<BrushRange>({ startIndex: 0, endIndex: 0 });
	const chartRef = useRef<HTMLDivElement>(null);

	const instrumentosOrdenados = [...instrumentos].sort((a, b) => a.nome.localeCompare(b.nome));
	const nomesInstrumentos = instrumentosOrdenados.map((instrumento) => instrumento.nome);
	const todosSelecionados = nomesInstrumentos.length > 0 && nomesInstrumentos.every((nome) => selecionados.includes(nome));

	const toggleInstrumento = (nome: string) => {
		setSelecionados((prev) =>
			prev.includes(nome) ? prev.filter((item) => item !== nome) : [...prev, nome]
		);
	};

	const toggleTodos = () => {
		setSelecionados((prev) => (prev.length === nomesInstrumentos.length ? [] : nomesInstrumentos));
	};

	const handleBrushDragEnd = (event: BrushDragEndInput) => {
		const { startIndex, endIndex } = getBrushDragEndPayload(event);

		setCommittedBrushRange((prev) => {
			const maxIndex = Math.max(dataNormalizada.length - 1, 0);
			const nextStart = typeof startIndex === 'number'
				? Math.max(0, Math.min(startIndex, maxIndex))
				: prev.startIndex;
			const nextEnd = typeof endIndex === 'number'
				? Math.max(nextStart, Math.min(endIndex, maxIndex))
				: prev.endIndex;

			return { startIndex: nextStart, endIndex: nextEnd };
		});
	};

	const handleLimpar = async () => {
		setLoading(true);
		if (confId) {
			try {
				const res = await chartsConf.delet(confId);
				if (res instanceof Error) {
					alert(`Erro ao deletar: ${res.message}`, 'error');
				} else {
					setTimeout(() => {
						setYMinCustom('');
						setYMaxCustom('');
						setYMinAplicado(null);
						setYMaxAplicado(null);
						setLoading(false);
					}, 500);
				}
			} catch (error) {
				alert(`Erro ao deletar: ${error}`, 'error');
			}
		} else {
			setTimeout(() => {
				setYMinCustom('');
				setYMaxCustom('');
				setYMinAplicado(null);
				setYMaxAplicado(null);
				setLoading(false);
			}, 500);
		}
	};

	async function saveConfig(data: chartsConfigData, id_barragem: string) {
		try {
			const res = await chartsConf.create(id_barragem, data);
			if (res instanceof Error) {
				alert('Erro ao carregar configuracoes do grafico!', 'error');
			} else {
				alert('Configuracoes salvas!', 'success');
			}
		} catch (error) {
			alert('Erro ao carregar configuracoes do grafico!', 'error');
		}
	}

	const handleSaveConfig = async () => {
		const config = {
			eixo_y_max: yMaxAplicado,
			eixo_y_min: yMinAplicado,
		};
		const jsonConfig = JSON.stringify(config);

		const dataConf = {
			tipo_grafico: tipo_instrumento,
			instrumentos_ativos: selecionados,
			configuracoes: jsonConfig,
		};
		await saveConfig(dataConf, barragem_id);
	};

	useEffect(() => {
		if (confGrafico) {
			const confJson = confGrafico[0].configuracoes;
			const conf = JSON.parse(confJson);
			setYMaxCustom(conf.eixo_y_max ?? '');
			setYMinCustom(conf.eixo_y_min ?? '');
			setYMaxAplicado(conf.eixo_y_max ?? null);
			setYMinAplicado(conf.eixo_y_min ?? null);
			setConfId(confGrafico[0].uuid !== undefined ? confGrafico[0].uuid : null);
		} else {
			setYMaxCustom('');
			setYMinCustom('');
			setYMaxAplicado(null);
			setYMinAplicado(null);
			setConfId(null);
		}
	}, [confGrafico, tipo_instrumento]);

	const handleAplicar = () => {
		setLoading(true);
		setTimeout(() => {
			const minVal = parseNumber(yMinCustom);
			const maxVal = parseNumber(yMaxCustom);

			if (minVal !== null && maxVal !== null && minVal < maxVal) {
				setYMinAplicado(minVal);
				setYMaxAplicado(maxVal);
			}
			setLoading(false);
		}, 500);
	};

	const ativos = instrumentos.filter((instrumento) => selecionados.includes(instrumento.nome));
	const mostrarFaixas = ativos.length === 1;
	const unico = mostrarFaixas ? ativos[0] : null;

	const datasLeitura = useMemo(() => buildReadingDates(
		instrumentos.flatMap((instrumento) => instrumento.leituras.map((leitura) => new Date(leitura.data_leitura)))
	), [instrumentos]);
	const leiturasPorInstrumento = useMemo(() => instrumentos.map((instrumento) => ({
		instrumento,
		leiturasPorData: buildReadingLookup(instrumento.leituras),
	})), [instrumentos]);

	const dataNormalizada = useMemo(() => datasLeitura.map((date) => {
		const dateKey = getChartDateKey(date);
		const ponto: any = { data_leitura: dateKey };
		leiturasPorInstrumento.forEach(({ instrumento, leiturasPorData }) => {
			const leitura = leiturasPorData.get(dateKey);

			const valorX = leitura ? parseNumber(leitura.deslocamento_x) : null;
			const valorY = leitura ? parseNumber(leitura.deslocamento_y) : null;
			const valorZ = leitura ? parseNumber(leitura.deslocamento_z) : null;

			if (yMinAplicado !== null && yMaxAplicado !== null) {
				ponto[`${instrumento.nome}_x`] = (valorX !== null && (valorX < yMinAplicado || valorX > yMaxAplicado)) ? null : valorX;
				ponto[`${instrumento.nome}_y`] = (valorY !== null && (valorY < yMinAplicado || valorY > yMaxAplicado)) ? null : valorY;
				ponto[`${instrumento.nome}_z`] = (valorZ !== null && (valorZ < yMinAplicado || valorZ > yMaxAplicado)) ? null : valorZ;
				return;
			}

			ponto[`${instrumento.nome}_x`] = valorX;
			ponto[`${instrumento.nome}_y`] = valorY;
			ponto[`${instrumento.nome}_z`] = valorZ;
		});
		return ponto;
	}), [datasLeitura, leiturasPorInstrumento, yMinAplicado, yMaxAplicado]);

	const initialBrushRange = useMemo(() => getInitialBrushRange(datasLeitura), [datasLeitura]);

	const valoresXAtivos = ativos.flatMap((instrumento) => instrumento.leituras.map((leitura) => parseNumber(leitura.deslocamento_x)).filter((value): value is number => value !== null));
	const valoresYAtivos = ativos.flatMap((instrumento) => instrumento.leituras.map((leitura) => parseNumber(leitura.deslocamento_y)).filter((value): value is number => value !== null));
	const valoresZAtivos = ativos.flatMap((instrumento) => instrumento.leituras.map((leitura) => parseNumber(leitura.deslocamento_z)).filter((value): value is number => value !== null));

	const valoresXFallback = instrumentos.flatMap((instrumento) => instrumento.leituras.map((leitura) => parseNumber(leitura.deslocamento_x)).filter((value): value is number => value !== null));
	const valoresYFallback = instrumentos.flatMap((instrumento) => instrumento.leituras.map((leitura) => parseNumber(leitura.deslocamento_y)).filter((value): value is number => value !== null));
	const valoresZFallback = instrumentos.flatMap((instrumento) => instrumento.leituras.map((leitura) => parseNumber(leitura.deslocamento_z)).filter((value): value is number => value !== null));

	const domainXOriginal = getBounds(valoresXAtivos.length > 0 ? valoresXAtivos : valoresXFallback, unico?.limites.x ?? [], mostrarFaixas);
	const domainYOriginal = getBounds(valoresYAtivos.length > 0 ? valoresYAtivos : valoresYFallback, unico?.limites.y ?? [], mostrarFaixas);
	const domainZOriginal = getBounds(valoresZAtivos.length > 0 ? valoresZAtivos : valoresZFallback, unico?.limites.z ?? [], mostrarFaixas);

	const domainX: [number, number] = yMinAplicado !== null && yMaxAplicado !== null
		? [yMinAplicado, yMaxAplicado]
		: domainXOriginal;

	const domainY: [number, number] = yMinAplicado !== null && yMaxAplicado !== null
		? [yMinAplicado, yMaxAplicado]
		: domainYOriginal;

	const domainZ: [number, number] = yMinAplicado !== null && yMaxAplicado !== null
		? [yMinAplicado, yMaxAplicado]
		: domainZOriginal;

	const downloadChart = async () => {
		if (!chartRef.current || ativos.length === 0) {
			return;
		}

		const canvas = await html2canvas(chartRef.current);
		const link = document.createElement('a');
		link.download = `grafico-${selecionados.join('-')}.png`;
		link.href = canvas.toDataURL('image/png');
		link.click();
	};

	useEffect(() => {
		setSelecionados([]);
	}, [tipo_instrumento]);

	useEffect(() => {
		setCommittedBrushRange(initialBrushRange);
	}, [
		tipo_instrumento,
		datasLeitura.length,
		datasLeitura[0]?.getTime(),
		datasLeitura[datasLeitura.length - 1]?.getTime(),
		initialBrushRange.startIndex,
		initialBrushRange.endIndex,
	]);

	return (
		<Box style={{ fontFamily: 'Roboto, sans-serif' }}>
			<style>{`
				@keyframes spin {
					0% { transform: rotate(0deg); }
					100% { transform: rotate(360deg); }
				}
			`}</style>

			<div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '16px', alignItems: 'center' }}>
				<label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600 }}>
					<input
						type="checkbox"
						checked={todosSelecionados}
						onChange={toggleTodos}
						disabled={nomesInstrumentos.length === 0}
					/>
					<span>Marcar todos</span>
				</label>
				{instrumentosOrdenados.map((instrumento) => (
					<label key={instrumento.nome} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						<input
							type="checkbox"
							checked={selecionados.includes(instrumento.nome)}
							onChange={() => toggleInstrumento(instrumento.nome)}
						/>
						<span>{instrumento.nome}</span>
					</label>
				))}
				<IconButton onClick={downloadChart} disabled={ativos.length === 0}>
					<Download />
				</IconButton>
			</div>

			<div style={{ display: 'flex', gap: '20px' }}>
				<div style={{ width: '200px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
					<Typography variant='h6'>Editar Eixo Y</Typography>

					<div>
						<Typography variant='caption'>Maximo</Typography>
						<Input
							type="number"
							value={yMaxCustom}
							onChange={(e) => setYMaxCustom(e.target.value)}
							style={{
								width: '100%',
								padding: '8px',
								border: '1px solid #ccc',
								borderRadius: '4px',
								fontSize: '14px'
							}}
						/>
					</div>

					<div>
						<Typography variant='caption'>Minimo</Typography>
						<Input
							type="number"
							value={yMinCustom}
							onChange={(e) => setYMinCustom(e.target.value)}
							style={{
								width: '100%',
								padding: '8px',
								border: '1px solid #ccc',
								borderRadius: '4px',
								fontSize: '14px'
							}}
						/>
					</div>

					<Button
						onClick={handleAplicar}
						disabled={!yMinCustom || !yMaxCustom}
						variant='contained'
						startIcon={<Check />}
					>
						Aplicar
					</Button>

					<div style={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: 4 }}>
						<Button
							onClick={handleSaveConfig}
							variant='contained'
							startIcon={<Save />}
							disabled={yMaxAplicado === null || yMinAplicado === null}
						>
							Salvar
						</Button>
						<Button
							onClick={handleLimpar}
							variant='contained'
							style={{ backgroundColor: '#d13838' }}
							startIcon={<Clear />}
						>
							Limpar
						</Button>
					</div>
				</div>

				<div ref={chartRef} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '40px' }}>
					<div>
						<Typography variant='subtitle1' style={{ marginBottom: '8px', fontWeight: 500 }}>
							Deslocamento X
						</Typography>
						<EixoGrafico
							data={dataNormalizada}
							label="Deslocamento X (mm)"
							campo="x"
							ativos={ativos}
							mostrarFaixas={mostrarFaixas}
							limites={unico?.limites.x ?? []}
							domain={domainX}
							loading={loading}
							showBrush={false}
							brushRange={committedBrushRange}
						/>
					</div>

					<div>
						<Typography variant='subtitle1' style={{ marginBottom: '8px', fontWeight: 500 }}>
							Deslocamento Y
						</Typography>
						<EixoGrafico
							data={dataNormalizada}
							label="Deslocamento Y (mm)"
							campo="y"
							ativos={ativos}
							mostrarFaixas={mostrarFaixas}
							limites={unico?.limites.y ?? []}
							domain={domainY}
							loading={loading}
							showBrush={false}
							brushRange={committedBrushRange}
						/>
					</div>

					<div>
						<Typography variant='subtitle1' style={{ marginBottom: '8px', fontWeight: 500 }}>
							Deslocamento Z
						</Typography>
						<EixoGrafico
							data={dataNormalizada}
							label="Deslocamento Z (mm)"
							campo="z"
							ativos={ativos}
							mostrarFaixas={mostrarFaixas}
							limites={unico?.limites.z ?? []}
							domain={domainZ}
							loading={loading}
							showBrush
							brushRange={committedBrushRange}
							onBrushDragEnd={handleBrushDragEnd}
						/>
					</div>
				</div>
			</div>
		</Box>
	);
};

export default GraficoLinhasMarcoMulti;
